import { createContext, ReactNode, useContext } from 'react';

/**
 * Context that provides the preferred display currency for chart components.
 *
 * The value is `undefined` when no override is needed (i.e., the metric field does not
 * have the `'multi-currency'` flag, or the user's preferred currency is USD).
 * Chart components pass this value to `getCurrencyAwareFormatter` so that money values
 * are formatted in the correct currency without each chart needing to know about field flags
 * or the user preference service.
 */
const PreferredCurrencyContext = createContext<string | undefined>(undefined);

/**
 * Provides a preferred display currency to descendant chart components.
 * Pass `undefined` (or omit `currency`) to indicate that no currency override is needed.
 */
export function PreferredCurrencyProvider({ currency, children }: { currency: string | undefined; children: ReactNode }) {
    return <PreferredCurrencyContext.Provider value={currency}>{children}</PreferredCurrencyContext.Provider>;
}

/**
 * Returns the preferred display currency for the current chart context, or `undefined`
 * if no override is in effect (meaning the chart should format money as USD / default).
 *
 * Chart components pass this to `getCurrencyAwareFormatter` alongside the format name.
 */
export function usePreferredCurrency(): string | undefined {
    return useContext(PreferredCurrencyContext);
}
